import {
  Heading,
  Input,
  Stack,
  useToast,
  Textarea,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useDisclosure,
  Button,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useCallback, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PrimayButton } from "../../components/Button";
import { FileUploadModal } from "../../components/FileUploadModal";
import { TimePeriodPicker } from "../../components/TimePeriodPicker";
import { useStores } from "../../hooks/useStores";
import { SettingsEnum, SupervisionedWorkoutsTimetable } from "../../types";

export const SupervisionedWorkoutsForm: React.FC = observer(() => {
  const { session } = useStores();
  const toast = useToast();
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const DAYS_OF_WEEK = [0, 1, 2, 3, 4, 5, 6];

  const [
    editedSupervisionedWorkoutStep1Video,
    setSupervisionedWorkoutStep1Video,
  ] = useState("");
  const [
    editedSupervisionedWorkoutStep1Message,
    setEditedSupervisionedWorkoutStep1Message,
  ] = useState("");

  const [
    editedSupervisionedWorkoutStep2ZoomLink,
    setSupervisionedWorkoutStep2ZoomLink,
  ] = useState("");
  const [
    editedSupervisionedWorkoutStep2Message,
    setEditedSupervisionedWorkoutStep2Message,
  ] = useState("");

  const [
    editedSupervisionedWorkoutStep3Video,
    setSupervisionedWorkoutStep3Video,
  ] = useState("");
  const [
    editedSupervisionedWorkoutStep3Message,
    setEditedSupervisionedWorkoutStep3Message,
  ] = useState("");

  const [
    editedSupervisionedWorkoutTimetable,
    setEditedSupervisionedWorkoutTimetable,
  ] = useState<SupervisionedWorkoutsTimetable | undefined>(
    session.supervisionedWorkoutTimetable
  );

  const [editedWorkoutsTimetable, setEditedWorkoutsTimetable] = useState("");

  useEffect(() => {
    if (session.supervisionedWorkoutTimetable) {
      setEditedSupervisionedWorkoutTimetable(
        session.supervisionedWorkoutTimetable
      );
    }
  }, [session.supervisionedWorkoutTimetable]);

  const handleSubmitSupervisionedWorkoutStep1Video = useCallback(async () => {
    try {
      await session.updateSettings(
        SettingsEnum.SUPERVISED_WORKOUTS_STEP_1_VIDEO_URL,
        editedSupervisionedWorkoutStep1Video || ""
      );
      toast({
        title: t("common.actionExecutedSuccessfully"),
        position: "bottom",
        status: "success",
      });
    } catch (err) {
      toast({
        title: t("common.error"),
        position: "bottom",
        status: "error",
      });
    }
  }, [editedSupervisionedWorkoutStep1Video, session, t, toast]);

  const handleSubmitSupervisionedWorkoutStep1Message = useCallback(async () => {
    try {
      await session.updateSettings(
        SettingsEnum.SUPERVISED_WORKOUTS_STEP_1_MESSAGE,
        editedSupervisionedWorkoutStep1Message || ""
      );
      toast({
        title: t("common.actionExecutedSuccessfully"),
        position: "bottom",
        status: "success",
      });
    } catch (err) {
      toast({
        title: t("common.error"),
        position: "bottom",
        status: "error",
      });
    }
  }, [editedSupervisionedWorkoutStep1Message, session, t, toast]);

  const handleSubmitSupervisionedWorkoutStep2ZoomLink =
    useCallback(async () => {
      try {
        await session.updateSettings(
          SettingsEnum.SUPERVISED_WORKOUTS_STEP_2_ZOOM_LINK,
          editedSupervisionedWorkoutStep2ZoomLink || ""
        );
        toast({
          title: t("common.actionExecutedSuccessfully"),
          position: "bottom",
          status: "success",
        });
      } catch (err) {
        toast({
          title: t("common.error"),
          position: "bottom",
          status: "error",
        });
      }
    }, [editedSupervisionedWorkoutStep2ZoomLink, session, t, toast]);

  const handleSubmitSupervisionedWorkoutStep2Message = useCallback(async () => {
    try {
      await session.updateSettings(
        SettingsEnum.SUPERVISED_WORKOUTS_STEP_2_MESSAGE,
        editedSupervisionedWorkoutStep2Message || ""
      );
      toast({
        title: t("common.actionExecutedSuccessfully"),
        position: "bottom",
        status: "success",
      });
    } catch (err) {
      toast({
        title: t("common.error"),
        position: "bottom",
        status: "error",
      });
    }
  }, [editedSupervisionedWorkoutStep2Message, session, t, toast]);

  const handleSubmitSupervisionedWorkoutStep3Video = useCallback(async () => {
    try {
      await session.updateSettings(
        SettingsEnum.SUPERVISED_WORKOUTS_STEP_3_VIDEO_URL,
        editedSupervisionedWorkoutStep3Video || ""
      );
      toast({
        title: t("common.actionExecutedSuccessfully"),
        position: "bottom",
        status: "success",
      });
    } catch (err) {
      toast({
        title: t("common.error"),
        position: "bottom",
        status: "error",
      });
    }
  }, [editedSupervisionedWorkoutStep3Video, session, t, toast]);

  const handleSubmitSupervisionedWorkoutStep3Message = useCallback(async () => {
    try {
      await session.updateSettings(
        SettingsEnum.SUPERVISED_WORKOUTS_STEP_3_MESSAGE,
        editedSupervisionedWorkoutStep3Message || ""
      );
      toast({
        title: t("common.actionExecutedSuccessfully"),
        position: "bottom",
        status: "success",
      });
    } catch (err) {
      toast({
        title: t("common.error"),
        position: "bottom",
        status: "error",
      });
    }
  }, [editedSupervisionedWorkoutStep3Message, session, t, toast]);

  const handleChangeWorkputTimetable = useCallback(
    (
      dayOfWeek: number,
      slot: string,
      period: { from: Date | null; to: Date | null }
    ) => {
      const currentValue = { ...editedSupervisionedWorkoutTimetable };
      currentValue[dayOfWeek][slot] = period;
      setEditedSupervisionedWorkoutTimetable(currentValue);
    },
    [editedSupervisionedWorkoutTimetable]
  );

  const handleSubmitSupervisionedWorkoutTimetable = useCallback(async () => {
    try {
      await session.updateSettings(
        SettingsEnum.SUPERVISED_WORKOUTS_TIMETABLES,
        JSON.stringify(editedSupervisionedWorkoutTimetable)
      );
      toast({
        title: t("common.actionExecutedSuccessfully"),
        position: "bottom",
        status: "success",
      });
    } catch (err) {
      toast({
        title: t("common.error"),
        position: "bottom",
        status: "error",
      });
    }
  }, [editedSupervisionedWorkoutTimetable, session, t, toast]);

  const handleSubmitdWorkoutsTimetable = useCallback(async () => {
    try {
      await session.updateSettings(
        SettingsEnum.WORKOUTS_TIMETABLES,
        editedWorkoutsTimetable || ""
      );
      toast({
        title: t("common.actionExecutedSuccessfully"),
        position: "bottom",
        status: "success",
      });
    } catch (err) {
      toast({
        title: t("common.error"),
        position: "bottom",
        status: "error",
      });
    }
  }, [editedWorkoutsTimetable, session, t, toast]);

  return (
    <Stack marginTop={"2rem"}>
      <Heading as="h5" size="sm" marginBottom={"1rem"}>
        {t("screens.adminSettings.supervisionedWorkouts.title")}
      </Heading>

      <Stack>
        <Heading as="h6" size="xs">
          {t("screens.adminSettings.supervisionedWorkouts.step1Video")}
        </Heading>
        <Stack gridGap={"1"} marginBottom={"2rem"} marginTop={"1rem"}>
          <Input
            value={
              editedSupervisionedWorkoutStep1Video.length > 0
                ? editedSupervisionedWorkoutStep1Video
                : session.supervisionedWorkoutStep1Video?.value || ""
            }
            onChange={(e) => setSupervisionedWorkoutStep1Video(e.target.value)}
          />

          <PrimayButton
            onClick={handleSubmitSupervisionedWorkoutStep1Video}
            isLoading={false}
            disabled={editedSupervisionedWorkoutStep1Video.length === 0}
          >
            {t("common.save")}
          </PrimayButton>
        </Stack>
      </Stack>

      <Stack paddingTop={"1rem"}>
        <Heading as="h6" size="xs">
          {t("screens.adminSettings.supervisionedWorkouts.step1Message")}
        </Heading>
        <Stack gridGap={"1"} marginBottom={"2rem"} marginTop={"1rem"}>
          <Textarea
            value={
              editedSupervisionedWorkoutStep1Message.length > 0
                ? editedSupervisionedWorkoutStep1Message
                : session.supervisionedWorkoutStep1Message?.value || ""
            }
            onChange={(e) =>
              setEditedSupervisionedWorkoutStep1Message(e.target.value)
            }
            placeholder="Indica il messaggio che sarà visualizzato agli utenti"
          />

          <PrimayButton
            onClick={handleSubmitSupervisionedWorkoutStep1Message}
            isLoading={false}
            disabled={editedSupervisionedWorkoutStep1Message.length === 0}
          >
            {t("common.save")}
          </PrimayButton>
        </Stack>
      </Stack>

      <Stack>
        <Heading as="h6" size="xs" paddingTop={"2rem"}>
          {t("screens.adminSettings.supervisionedWorkouts.step2ZoomLink")}
        </Heading>
        <Stack gridGap={"1"} marginBottom={"2rem"} marginTop={"1rem"}>
          <Input
            value={
              editedSupervisionedWorkoutStep2ZoomLink.length > 0
                ? editedSupervisionedWorkoutStep2ZoomLink
                : session.supervisionedWorkoutStep2ZoomLink?.value || ""
            }
            onChange={(e) =>
              setSupervisionedWorkoutStep2ZoomLink(e.target.value)
            }
          />

          <PrimayButton
            onClick={handleSubmitSupervisionedWorkoutStep2ZoomLink}
            isLoading={false}
            disabled={editedSupervisionedWorkoutStep2ZoomLink.length === 0}
          >
            {t("common.save")}
          </PrimayButton>
        </Stack>
      </Stack>

      <Stack paddingTop={"1rem"}>
        <Heading as="h6" size="xs">
          {t("screens.adminSettings.supervisionedWorkouts.step2Message")}
        </Heading>
        <Stack gridGap={"1"} marginBottom={"2rem"} marginTop={"1rem"}>
          <Textarea
            value={
              editedSupervisionedWorkoutStep2Message.length > 0
                ? editedSupervisionedWorkoutStep2Message
                : session.supervisionedWorkoutStep2Message?.value || ""
            }
            onChange={(e) =>
              setEditedSupervisionedWorkoutStep2Message(e.target.value)
            }
            placeholder="Indica il messaggio che sarà visualizzato agli utenti"
          />

          <PrimayButton
            onClick={handleSubmitSupervisionedWorkoutStep2Message}
            isLoading={false}
            disabled={editedSupervisionedWorkoutStep2Message.length === 0}
          >
            {t("common.save")}
          </PrimayButton>
        </Stack>
      </Stack>

      <Stack>
        <Heading as="h6" size="xs" paddingTop={"2rem"}>
          {t("screens.adminSettings.supervisionedWorkouts.step3Video")}
        </Heading>
        <Stack gridGap={"1"} marginBottom={"2rem"} marginTop={"1rem"}>
          <Input
            value={
              editedSupervisionedWorkoutStep3Video.length > 0
                ? editedSupervisionedWorkoutStep3Video
                : session.supervisionedWorkoutStep3Video?.value || ""
            }
            onChange={(e) => setSupervisionedWorkoutStep3Video(e.target.value)}
          />

          <PrimayButton
            onClick={handleSubmitSupervisionedWorkoutStep3Video}
            isLoading={false}
            disabled={editedSupervisionedWorkoutStep3Video.length === 0}
          >
            {t("common.save")}
          </PrimayButton>
        </Stack>
      </Stack>

      <Stack paddingTop={"1rem"}>
        <Heading as="h6" size="xs">
          {t("screens.adminSettings.supervisionedWorkouts.step3Message")}
        </Heading>
        <Stack gridGap={"1"} marginBottom={"2rem"} marginTop={"1rem"}>
          <Textarea
            value={
              editedSupervisionedWorkoutStep3Message.length > 0
                ? editedSupervisionedWorkoutStep3Message
                : session.supervisionedWorkoutStep3Message?.value || ""
            }
            onChange={(e) =>
              setEditedSupervisionedWorkoutStep3Message(e.target.value)
            }
            placeholder="Indica il messaggio che sarà visualizzato agli utenti"
          />

          <PrimayButton
            onClick={handleSubmitSupervisionedWorkoutStep3Message}
            isLoading={false}
            disabled={editedSupervisionedWorkoutStep3Message.length === 0}
          >
            {t("common.save")}
          </PrimayButton>
        </Stack>
      </Stack>

      <Stack paddingTop={"1rem"}>
        <Heading as="h6" size="xs">
          {t("screens.adminSettings.supervisionedWorkouts.timetables")}
        </Heading>
        <Stack gridGap={"1"} marginBottom={"2rem"} marginTop={"1rem"}>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>{t("screens.adminSettings.supervisionedWorkouts.day")}</Th>
                <Th>
                  {t("screens.adminSettings.supervisionedWorkouts.slot1")}
                </Th>
                <Th>
                  {t("screens.adminSettings.supervisionedWorkouts.slot2")}
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {editedSupervisionedWorkoutTimetable &&
                DAYS_OF_WEEK.map((dayOfWeek) => (
                  <Tr>
                    <Td>{t(`dayOfWeek.${dayOfWeek}`)}</Td>
                    <Td>
                      <TimePeriodPicker
                        fromCaption="Dalle ore"
                        toCaption="Alle ore"
                        value={{
                          from: editedSupervisionedWorkoutTimetable[dayOfWeek]
                            .slot1.from
                            ? new Date(
                                // @ts-ignore
                                editedSupervisionedWorkoutTimetable[
                                  dayOfWeek
                                ].slot1.from
                              )
                            : editedSupervisionedWorkoutTimetable[dayOfWeek]
                                .slot1.from,
                          to: editedSupervisionedWorkoutTimetable[dayOfWeek]
                            .slot1.to
                            ? new Date(
                                // @ts-ignore
                                editedSupervisionedWorkoutTimetable[
                                  dayOfWeek
                                ].slot1.to
                              )
                            : editedSupervisionedWorkoutTimetable[dayOfWeek]
                                .slot1.to,
                        }}
                        onChange={(period) =>
                          handleChangeWorkputTimetable(
                            dayOfWeek,
                            "slot1",
                            period
                          )
                        }
                      />
                    </Td>
                    <Td>
                      <TimePeriodPicker
                        fromCaption="Dalle ore"
                        toCaption="Alle ore"
                        value={{
                          from: editedSupervisionedWorkoutTimetable[dayOfWeek]
                            .slot2.from
                            ? new Date(
                                // @ts-ignore
                                editedSupervisionedWorkoutTimetable[
                                  dayOfWeek
                                ].slot2.from
                              )
                            : editedSupervisionedWorkoutTimetable[dayOfWeek]
                                .slot2.from,
                          to: editedSupervisionedWorkoutTimetable[dayOfWeek]
                            .slot2.to
                            ? new Date(
                                // @ts-ignore
                                editedSupervisionedWorkoutTimetable[
                                  dayOfWeek
                                ].slot2.to
                              )
                            : editedSupervisionedWorkoutTimetable[dayOfWeek]
                                .slot2.to,
                        }}
                        onChange={(period) =>
                          handleChangeWorkputTimetable(
                            dayOfWeek,
                            "slot2",
                            period
                          )
                        }
                      />
                    </Td>
                  </Tr>
                ))}
            </Tbody>
          </Table>

          <PrimayButton
            onClick={handleSubmitSupervisionedWorkoutTimetable}
            isLoading={false}
          >
            {t("common.save")}
          </PrimayButton>
        </Stack>
      </Stack>

      <Stack paddingTop={"1rem"}>
        <Heading as="h6" size="xs">
          {t("screens.adminSettings.timetables")}
        </Heading>
        <Stack gridGap={"1"} marginBottom={"2rem"} marginTop={"1rem"}>
          {(session.worksoutTimetable &&
            session.worksoutTimetable.value.length > 0) ||
          editedWorkoutsTimetable.length > 0 ? (
            <img
              src={
                editedWorkoutsTimetable.length > 0
                  ? editedWorkoutsTimetable
                  : session.worksoutTimetable?.value
              }
              alt="Orari"
            />
          ) : (
            <p>Nessuna immagine caricata</p>
          )}

          <Button variant={"ghost"} onClick={onOpen} marginTop={"0.5rem"}>
            {t("screens.adminSettings.uploadTimetable")}
          </Button>

          <FileUploadModal
            isOpen={isOpen}
            onClose={onClose}
            onFileUploaded={(imageUrls: string[]) => {
              setEditedWorkoutsTimetable(imageUrls[0]);
              onClose();
            }}
            accept={"image/jpeg, image/png"}
          />

          <PrimayButton
            onClick={handleSubmitdWorkoutsTimetable}
            isLoading={false}
            disabled={editedWorkoutsTimetable.length === 0}
          >
            {t("common.save")}
          </PrimayButton>
        </Stack>
      </Stack>
    </Stack>
  );
});
